import React, { useState, useCallback } from 'react';
import { useB2BTranslation } from '../hooks/useB2BTranslation';
import { Trash } from 'lucide-react';
import { useB2BAuth } from '../B2BAuthContext/B2BAuthContext';
import { api } from '../../../../utils/apiConfig';
import './B2BDeleteProfileButton.css';

interface B2BDeleteProfileButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'secondary' | 'outline';
  showIcon?: boolean;
  disabled?: boolean;
}

const B2BDeleteProfileButton: React.FC<B2BDeleteProfileButtonProps> = ({
  onSuccess,
  onError,
  className = '',
  size = 'medium',
  variant = 'primary',
  showIcon = true,
  disabled = false
}) => {
  const { t } = useB2BTranslation();
  const { logout } = useB2BAuth();
  
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const handleDeleteClick = useCallback((): void => {
    setShowDeleteConfirm(true);
  }, []);

  const cancelDelete = useCallback((): void => {
    setShowDeleteConfirm(false);
  }, []);

  const confirmDelete = useCallback(async (): Promise<void> => {
    setIsDeleting(true);
    setShowDeleteConfirm(false);

    try {
      const response = await api.deleteProfile();
      
      if (response.success) {
        // Call success callback if provided
        if (onSuccess) {
          onSuccess();
        }
        
        // Wait a moment to show the success message
        setTimeout(async () => {
          // Logout the user since their profile is deleted
          await logout();
        }, 2000);
      } else {
        const errorMessage = response.message || 'Failed to delete profile. Please try again.';
        if (onError) {
          onError(errorMessage);
        }
      }
    } catch (error: any) {
      console.error("Error deleting profile:", error);
      const errorMessage = error.response?.data?.message || 'Failed to delete profile. Please try again.';
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [logout, onSuccess, onError]);

  const buttonClasses = `b2b-delete-profile-button b2b-delete-profile-button--${size} b2b-delete-profile-button--${variant} ${className}`;

  return (
    <>
      <button
        onClick={handleDeleteClick}
        disabled={disabled || isDeleting}
        className={buttonClasses}
        type="button"
      >
        {isDeleting ? (
          <>
            <div className="b2b-delete-profile-button__spinner" aria-hidden="true" />
            {t('dashboard.profile.deleting')}
          </>
        ) : (
          <>
            {showIcon && <Trash size={16} aria-hidden="true" />}
            {t('buttons.deleteProfile')}
          </>
        )}
      </button>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="b2b-delete-modal-overlay">
          <div className="b2b-delete-modal">
            <div className="b2b-delete-modal-header">
              <div className="b2b-delete-modal-icon">
                <Trash size={24} />
              </div>
              <h3>{t('dashboard.profile.confirmDeleteTitle')}</h3>
            </div>
            <div className="b2b-delete-modal-content">
              <p>{t('dashboard.profile.confirmDeleteMessage')}</p>
              <div className="b2b-delete-modal-actions">
                <button
                  onClick={cancelDelete}
                  className="b2b-delete-modal-button b2b-delete-modal-button--secondary"
                  disabled={isDeleting}
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  className="b2b-delete-modal-button b2b-delete-modal-button--danger"
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? (
                      <>
                        <div className="b2b-delete-modal-button__spinner" aria-hidden="true" />
                        {t('dashboard.profile.deleting')}
                      </>
                    )
                    : t('buttons.deleteProfile')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default B2BDeleteProfileButton;
